'use client';
import { useEffect, useState } from 'react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const SW_VERSION = 'v7';

export default function ServiceWorkerRegistration() {
  const [installPrompt, setInstallPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showBanner, setShowBanner] = useState(false);
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;

    const registerSW = async () => {
      try {
        // Check existing registrations
        const registrations = await navigator.serviceWorker.getRegistrations();

        // Unregister any SW that is NOT our current sw.js (e.g. stale from old paths)
        for (const reg of registrations) {
          const swUrl = reg.active?.scriptURL || reg.installing?.scriptURL || reg.waiting?.scriptURL || '';
          // If it's not pointing to /sw.js, unregister it (stale/wrong path)
          if (swUrl && !swUrl.endsWith('/sw.js')) {
            await reg.unregister();
          }
        }

        // Register (or re-use existing) /sw.js with updateViaCache: 'none'
        // so the browser always fetches the latest sw.js from the network
        const registration = await navigator.serviceWorker.register('/sw.js', {
          scope: '/',
          updateViaCache: 'none',
        });

        // Force an update check so v7 is picked up immediately if v6 is active
        registration.update().catch(() => {});

        // Listen for SW messages
        navigator.serviceWorker.addEventListener('message', (event) => {
          if (event.data?.type === 'SW_UPDATED') {
            // New SW activated — page is already fresh, no forced reload needed
          }
        });
      } catch {
        // Non-fatal — app works fine without SW
      }
    };

    registerSW();

    // PWA install banner logic
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setInstalled(true);
      return;
    }

    const dismissed = sessionStorage.getItem('pwa-banner-dismissed');
    if (dismissed) return;

    const handler = (e: Event) => {
      e.preventDefault();
      setInstallPrompt(e as BeforeInstallPromptEvent);
      setTimeout(() => setShowBanner(true), 3000);
    };

    window.addEventListener('beforeinstallprompt', handler);
    window.addEventListener('appinstalled', () => {
      setShowBanner(false);
      setInstalled(true);
    });

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
    };
  }, []);

  const handleInstall = async () => {
    if (!installPrompt) return;
    await installPrompt.prompt();
    const { outcome } = await installPrompt.userChoice;
    if (outcome === 'accepted') {
      setShowBanner(false);
      setInstalled(true);
    }
    setInstallPrompt(null);
  };

  const handleDismiss = () => {
    setShowBanner(false);
    sessionStorage.setItem('pwa-banner-dismissed', '1');
  };

  if (!showBanner || installed) return null;

  return (
    <div
      role="banner"
      aria-label="Install app banner"
      className="fixed bottom-0 left-0 right-0 z-[9999] flex items-center justify-between gap-3 px-4 py-3 sm:px-6"
      style={{
        background: 'linear-gradient(135deg, #12121A 0%, #1a1a2e 100%)',
        borderTop: '1px solid rgba(255,215,0,0.35)',
        boxShadow: '0 -4px 24px rgba(0,0,0,0.6)'
      }}
    >
      {/* Icon + text */}
      <div className="flex items-center gap-3 min-w-0">
        <img
          src="/assets/images/1785348258951-1785357162387.jpg"
          alt="Maximum Effort MDJ app icon"
          className="w-10 h-10 rounded-xl flex-shrink-0 object-cover"
          style={{ border: '1px solid rgba(255,215,0,0.4)' }}
        />
        <div className="min-w-0">
          <p className="text-sm font-semibold text-white leading-tight truncate">
            Add to Home Screen
          </p>
          <p className="text-xs text-gray-400 leading-tight truncate">
            Max Effort MDJ — works offline, no app store needed
          </p>
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2 flex-shrink-0">
        <button
          onClick={handleDismiss}
          aria-label="Dismiss install banner"
          className="text-gray-500 hover:text-gray-300 transition-colors p-1"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        <button
          onClick={handleInstall}
          className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold transition-all active:scale-95"
          style={{
            background: 'linear-gradient(135deg, #FFD700, #FFA500)',
            color: '#0a0a0a'
          }}
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
          </svg>
          Install
        </button>
      </div>
    </div>
  );
}
